import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { CalendarIcon, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';

interface EquipmentData {
  tipo: string;
  identificacao: string;
  condicao?: string;
  operador?: string;
  tempoOcioso?: string;
  observations?: string;
  status?: 'proprio' | 'locado';
  quantity?: number;
  leaseExpiryDate?: string;
}

interface EditEquipmentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  equipment: EquipmentData | null;
  onSave: (updatedEquipment: EquipmentData) => void;
  onDelete: () => void;
}

export const EditEquipmentModal: React.FC<EditEquipmentModalProps> = ({
  open,
  onOpenChange,
  equipment,
  onSave,
  onDelete
}) => {
  const [quantity, setQuantity] = useState(1);
  const [leaseExpiryDate, setLeaseExpiryDate] = useState<Date | undefined>();
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  useEffect(() => {
    if (equipment) {
      setQuantity(equipment.quantity || 1);
      if (equipment.leaseExpiryDate) {
        setLeaseExpiryDate(new Date(equipment.leaseExpiryDate));
      } else {
        setLeaseExpiryDate(undefined);
      }
    }
  }, [equipment]);

  const handleSave = () => {
    if (!equipment) return;

    const updatedEquipment = {
      ...equipment,
      quantity,
      leaseExpiryDate: leaseExpiryDate ? leaseExpiryDate.toISOString() : equipment.leaseExpiryDate
    };

    onSave(updatedEquipment);
    onOpenChange(false);
  };

  const handleDelete = () => {
    onDelete();
    onOpenChange(false);
    setIsDeleteAlertOpen(false);
  };

  const handleCancel = () => {
    // Reset values to original
    if (equipment) {
      setQuantity(equipment.quantity || 1);
      if (equipment.leaseExpiryDate) {
        setLeaseExpiryDate(new Date(equipment.leaseExpiryDate));
      } else {
        setLeaseExpiryDate(undefined);
      }
    }
    onOpenChange(false);
  };

  if (!equipment) return null;

  const isRented = equipment.status === 'locado';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            Editar Equipamento
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Equipment Name - Read Only */}
          <div className="space-y-2">
            <Label htmlFor="equipment-name">Equipamento</Label>
            <Input
              id="equipment-name"
              value={equipment.tipo}
              readOnly
              className="bg-muted/50 cursor-default"
            />
          </div>

          {/* Quantity - Editable */}
          <div className="space-y-2">
            <Label htmlFor="quantity">Quantidade</Label>
            <Input
              id="quantity"
              type="number"
              min="1"
              value={quantity}
              onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
              className="w-full"
            />
          </div>

          {/* Lease Expiry Date - Only for rented equipment */}
          {isRented && (
            <div className="space-y-2">
              <Label>Data de Vencimento da Locação</Label>
              <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !leaseExpiryDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {leaseExpiryDate ? (
                      format(leaseExpiryDate, "dd/MM/yyyy")
                    ) : (
                      <span>Selecionar data</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={leaseExpiryDate}
                    onSelect={(date) => {
                      setLeaseExpiryDate(date);
                      setIsCalendarOpen(false);
                    }}
                    disabled={(date) => date < new Date()}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {/* Delete Button */}
          <AlertDialog open={isDeleteAlertOpen} onOpenChange={setIsDeleteAlertOpen}>
            <AlertDialogTrigger asChild>
              <Button
                variant="destructive"
                className="sm:mr-auto"
                onClick={() => setIsDeleteAlertOpen(true)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Deletar
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                <AlertDialogDescription>
                  Tem certeza que deseja deletar o equipamento "{equipment.tipo}"? 
                  Esta ação não pode ser desfeita.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={handleDelete}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Deletar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <div className="flex gap-2 sm:ml-auto">
            {/* Cancel Button */}
            <Button
              variant="outline"
              onClick={handleCancel}
            >
              Cancelar
            </Button>

            {/* Save Button */}
            <Button
              onClick={handleSave}
            >
              Salvar Alterações
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};